"use client";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  Pie,
  PieChart,
  Cell,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { FileText, CheckCircle2, Clock, XCircle, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../Component/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";
import API_URL from "../../../src/Config";
import "./allSection.css";
import axios from "axios";

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="newdashboard-tooltip">
        <p className="newdashboard-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function WorkPermitSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    department_id: "",
    permit_type_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  const [summary, setSummary] = useState({
    total_permits: 0,
    issued: 0,
    pending: 0,
    closed: 0,
    rejected: 0,
  });

  const [charts, setCharts] = useState({
    permitStatusData: [],
    permitTrendData: [],
    permitTypeData: [],
    departmentData: [],
  });

  const fetchWorkPermitData = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = new URLSearchParams();

      if (
        currentFilters.date_preset &&
        currentFilters.date_preset !== "custom" &&
        currentFilters.date_preset !== "All"
      ) {
        params.append("filter_option", currentFilters.date_preset);
      }
      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);
      if (currentFilters.permit_type_id)
        params.append("permit_type_id", currentFilters.permit_type_id);
      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);
      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const response = await axios.get(
        `${API_URL}/api/kpi/work-permit?${params.toString()}`,
        { headers: { Authorization: authKey } },
      );

      if (response.status === 200) {
        const data = response.data;
        console.log("data", data);
        // Set summary data
        setSummary({
          total_permits: data.summary.total_permits?.count ?? 0,
          issued: data.summary.approved_permits?.count ?? 0,
          pending: data.summary.pending_permits?.count ?? 0,
          closed: data.summary.closed_permits?.count ?? 0,
          rejected: data.summary.rejected_permits?.count ?? 0,
        });

        // Prepare pie chart data for status distribution
        const statusData = [
          {
            name: "Issued",
            value: data.summary.approved_permits?.count ?? 0,
            fill: "hsl(142, 71%, 45%)",
          },
          {
            name: "Pending",
            value: data.summary.pending_permits?.count ?? 0,
            fill: "hsl(25, 95%, 53%)",
          },
          {
            name: "Closed",
            value: data.summary.closed_permits?.count ?? 0,
            fill: "hsl(217, 91%, 60%)",
          },
          {
            name: "Rejected",
            value: data.summary.rejected_permits?.count ?? 0,
            fill: "hsl(0, 84%, 60%)",
          },
        ];

        // Transform weekly issuance datayy
        const weeklyData = (data.charts.weekly_issuance || []).map((item) => ({
          week: item.week,
          approved: item.permits_issued,
        }));

        // Transform permit type status data
        const permitTypeData = (data.charts.permit_type_status || []).map(
          (item) => ({
            type: item.permit_type,
            approved: item.approved,
            pending: item.pending,
            closed: item.closed,
            rejected: item.rejected,
            total: item.total,
          }),
        );

        // Transform department distribution data
        const departmentData = (data.charts.department_distribution || []).map(
          (item) => ({
            site: item.department,
            issued: item.approved,
            pending: item.pending,
            closed: item.closed,
            rejected: item.rejected,
          }),
        );

        setCharts({
          permitStatusData: statusData,
          permitTrendData: weeklyData,
          permitTypeData: permitTypeData,
          departmentData: departmentData,
        });
      }
    } catch (error) {
      console.error("Error fetching Work Permit data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchWorkPermitData(newFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      permit_type_id: "",
      date_preset: "All",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchWorkPermitData(reset);
  };

  useEffect(() => {
    fetchWorkPermitData();
  }, []);

  // Card click handler
  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);

    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }

    if (filters.permit_type_id && filters.permit_type_id.trim() !== "") {
      params.append("permit_type_id", filters.permit_type_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log(
      "Navigating to Work Permit List with params:",
      params.toString(),
    );
    // navigate(`/work-permit-list?${params.toString()}`);

    window.open(`/work-permit-list?${params.toString()}`, "_blank");
  };

  if (loading)
    return (
      <div className="loader-container">
        <div className="loader"></div>Loading...
      </div>
    );

  return (
    <div className="newdashboard-workpermit-section">
      {/* Header */}
      <div className="newdashboard-workpermit-header">
        <div>
          <h2 className="newdashboard-section-title">Work Permit Management</h2>
          <p>Track work permits and safety compliance</p>
        </div>
        <DashboardFilters
          filters={filters}
          onFilterChange={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          showPermitType={true}
        />
      </div>

      {/* KPI Cards - Now Clickable */}
      <div className="newdashboard-workpermit-kpis">
        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("all")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-workpermit-card-header">
            <CardTitle className="newdashboard-workpermit-card-title">
              Total Permits
            </CardTitle>
            <FileText className="newdashboard-workpermit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-workpermit-card-value">
              {summary.total_permits}
            </div>
            <p className="newdashboard-workpermit-note">All permits</p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("approved")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-workpermit-card-header">
            <CardTitle className="newdashboard-workpermit-card-title">
              Issued
            </CardTitle>
            <CheckCircle2 className="newdashboard-workpermit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-workpermit-card-value newdashboard-workpermit-value-approved">
              {summary.issued}
            </div>
            <p className="newdashboard-workpermit-note">
              {summary.total_permits > 0
                ? ((summary.issued / summary.total_permits) * 100).toFixed(1)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("pending")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-workpermit-card-header">
            <CardTitle className="newdashboard-workpermit-card-title">
              Pending
            </CardTitle>
            <Clock className="newdashboard-workpermit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-workpermit-card-value newdashboard-workpermit-value-pending">
              {summary.pending}
            </div>
            <p className="newdashboard-workpermit-note">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("closed")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-workpermit-card-header">
            <CardTitle className="newdashboard-workpermit-card-title">
              Closed
            </CardTitle>
            <Shield className="newdashboard-workpermit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-workpermit-card-value newdashboard-workpermit-value-closed">
              {summary.closed}
            </div>
            <p className="newdashboard-workpermit-note">Work completed</p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("rejected")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-workpermit-card-header">
            <CardTitle className="newdashboard-workpermit-card-title">
              Rejected
            </CardTitle>
            <XCircle className="newdashboard-workpermit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-workpermit-card-value newdashboard-workpermit-value-rejected">
              {summary.rejected}
            </div>
            <p className="newdashboard-workpermit-note">Safety concerns</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="newdashboard-workpermit-charts">
        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Permit Status Distribution</CardTitle>
            <CardDescription>Issued, Pending, Closed, Rejected</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer className="newdashboard-workpermit-chart">
              {charts.permitStatusData?.length > 0 &&
              charts.permitStatusData.some((d) => d.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.permitStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      dataKey="value"
                    >
                      {charts.permitStatusData?.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Permit Issuance</CardTitle>
            <CardDescription>Permits issued per week</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer className="newdashboard-workpermit-chart">
              {charts.permitTrendData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={charts.permitTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <ChartTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="issued"
                      stroke="hsl(217, 91%, 60%)"
                      strokeWidth={2}
                      name="Permits Issued"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Bar Chart by Type */}
      <Card>
        <CardHeader>
          <CardTitle>Permit Status by Type</CardTitle>
          <CardDescription>Breakdown by permit category</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer className="newdashboard-workpermit-chart">
            {charts.permitTypeData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.permitTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <ChartTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="approved"
                    fill="hsl(142, 71%, 45%)"
                    name="Issued"
                  />
                  <Bar
                    dataKey="pending"
                    fill="hsl(25, 95%, 53%)"
                    name="Pending"
                  />
                  <Bar
                    dataKey="closed"
                    fill="hsl(217, 91%, 60%)"
                    name="Closed"
                  />
                  <Bar
                    dataKey="rejected"
                    fill="hsl(0, 84%, 60%)"
                    name="Rejected"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Bar Chart by Department */}
      <Card>
        <CardHeader>
          <CardTitle>Permit Status by Department</CardTitle>
          <CardDescription>Department-wise permit distribution</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer className="newdashboard-workpermit-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.departmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="site" />
                <YAxis />
                <ChartTooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="issued"
                  fill="hsl(142, 71%, 45%)"
                  stackId="a"
                  name="Issued"
                />
                <Bar
                  dataKey="pending"
                  fill="hsl(25, 95%, 53%)"
                  stackId="a"
                  name="Pending"
                />
                <Bar
                  dataKey="closed"
                  fill="hsl(217, 91%, 60%)"
                  stackId="a"
                  name="Closed"
                />
                <Bar
                  dataKey="rejected"
                  fill="hsl(0, 84%, 60%)"
                  stackId="a"
                  name="Rejected"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// -------- with po without filter -------------

// "use client";
// import React, { useEffect, useState } from "react";
// import {
//   Bar,
//   BarChart,
//   Pie,
//   PieChart,
//   Cell,
//   Line,
//   LineChart,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   Legend,
// } from "recharts";
// import { FileText, CheckCircle2, Clock, XCircle, TrendingUp, Package } from "lucide-react";
// import {
//   Card,
//   CardHeader,
//   CardTitle,
//   CardContent,
//   CardDescription,
// } from "../../Component/ui/card";

// // Custom Tooltip Component
// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload && payload.length) {
//     return (
//       <div style={{
//         backgroundColor: 'white',
//         padding: '12px',
//         border: '1px solid #ccc',
//         borderRadius: '4px',
//         boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
//       }}>
//         <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
//         {payload.map((entry, index) => (
//           <p key={index} style={{ color: entry.color, margin: '4px 0' }}>
//             {entry.name}: {entry.value}
//           </p>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

// export default function PurchaseOrderSection() {
//   const [loading, setLoading] = useState(false);

//   // Mock data based on the API response structure
//   const [summary, setSummary] = useState({
//     total_prs: 31,
//     approved_prs: 17,
//     pending_prs: 8,
//     overdue_prs: 8,
//     critical_overdue: 8,
//     total_pos: 14,
//     open_pos: 4,
//     closed_pos: 10,
//     conversion_rate: 82.35
//   });

//   const [charts, setCharts] = useState({
//     prStatusFunnel: [
//       { name: "Total PRs", value: 31, fill: "hsl(217, 91%, 60%)" },
//       { name: "Approved", value: 17, fill: "hsl(142, 71%, 45%)" },
//       { name: "Pending", value: 8, fill: "hsl(25, 95%, 53%)" },
//       { name: "Overdue", value: 8, fill: "hsl(0, 84%, 60%)" }
//     ],
//     poStatusData: [
//       { name: "Open", value: 4, fill: "hsl(25, 95%, 53%)" },
//       { name: "Closed", value: 10, fill: "hsl(142, 71%, 45%)" }
//     ],
//     monthlyPOTrend: [
//       { month: "May 2025", count: 2 },
//       { month: "Jun 2025", count: 2 }
//     ],
//     topVendors: [
//       { vendor: "Flex printers", count: 4 },
//       { vendor: "gfhgfh", count: 3 },
//       { vendor: "Switchgear Services", count: 2 },
//       { vendor: "Sterling Pvt Ltd", count: 2 }
//     ],
//     overdueDepartments: [
//       { department: "Maintenance", count: 4 },
//       { department: "Mechanical", count: 3 },
//       { department: "Communication", count: 1 }
//     ],
//     prAgeAnalysis: [
//       { age_range: "Over 30 days", count: 8 }
//     ]
//   });

//   // Simulate API call
//   useEffect(() => {
//     setLoading(true);
//     // Simulate loading
//     setTimeout(() => {
//       setLoading(false);
//     }, 500);
//   }, []);

//   if (loading) {
//     return (
//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
//         <div style={{
//           border: '4px solid #f3f3f3',
//           borderTop: '4px solid #3498db',
//           borderRadius: '50%',
//           width: '40px',
//           height: '40px',
//           animation: 'spin 1s linear infinite'
//         }}></div>
//         <p>Loading...</p>
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '24px', backgroundColor: '#f9fafb' }}>
//       {/* Header */}
//       <div style={{ marginBottom: '32px' }}>
//         <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
//           Purchase Order Management
//         </h2>
//         <p style={{ color: '#6b7280' }}>Track purchase requests and orders</p>
//       </div>

//       {/* KPI Cards - Purchase Requests */}
//       <div style={{ marginBottom: '24px' }}>
//         <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>Purchase Requests</h3>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Total PRs Raised
//               </CardTitle>
//               <FileText style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
//                 {summary.total_prs}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>All time</p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Approved PRs
//               </CardTitle>
//               <CheckCircle2 style={{ width: '20px', height: '20px', color: '#22c55e' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#22c55e' }}>
//                 {summary.approved_prs}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
//                 {((summary.approved_prs / summary.total_prs) * 100).toFixed(1)}% of total
//               </p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Pending PRs
//               </CardTitle>
//               <Clock style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>
//                 {summary.pending_prs}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Awaiting approval</p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Overdue PRs
//               </CardTitle>
//               <XCircle style={{ width: '20px', height: '20px', color: '#ef4444' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444' }}>
//                 {summary.overdue_prs}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Pending &gt; 7 days</p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Critical Overdue
//               </CardTitle>
//               <XCircle style={{ width: '20px', height: '20px', color: '#dc2626' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>
//                 {summary.critical_overdue}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Pending &gt; 30 days</p>
//             </CardContent>
//           </Card>
//         </div>
//       </div>

//       {/* KPI Cards - Purchase Orders */}
//       <div style={{ marginBottom: '24px' }}>
//         <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>Purchase Orders</h3>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Total POs Generated
//               </CardTitle>
//               <Package style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
//                 {summary.total_pos}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>All time</p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Open POs
//               </CardTitle>
//               <Clock style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>
//                 {summary.open_pos}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
//                 {((summary.open_pos / summary.total_pos) * 100).toFixed(1)}% pending inward
//               </p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 Closed POs
//               </CardTitle>
//               <CheckCircle2 style={{ width: '20px', height: '20px', color: '#22c55e' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#22c55e' }}>
//                 {summary.closed_pos}
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
//                 {((summary.closed_pos / summary.total_pos) * 100).toFixed(1)}% completed
//               </p>
//             </CardContent>
//           </Card>

//           <Card>
//             <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px' }}>
//               <CardTitle style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
//                 PR to PO Conversion
//               </CardTitle>
//               <TrendingUp style={{ width: '20px', height: '20px', color: '#8b5cf6' }} />
//             </CardHeader>
//             <CardContent>
//               <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8b5cf6' }}>
//                 {summary.conversion_rate}%
//               </div>
//               <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Conversion efficiency</p>
//             </CardContent>
//           </Card>
//         </div>
//       </div>

//       {/* Charts Row 1 */}
//       <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
//         {/* PR Status Funnel */}
//         <Card>
//           <CardHeader>
//             <CardTitle>PR Status Distribution</CardTitle>
//             <CardDescription>Overview of purchase request statuses</CardDescription>
//           </CardHeader>
//           <CardContent>
//             <div style={{ height: '300px' }}>
//               {charts.prStatusFunnel?.length > 0 ? (
//                 <ResponsiveContainer width="100%" height="100%">
//                   <PieChart>
//                     <Pie
//                       data={charts.prStatusFunnel}
//                       cx="50%"
//                       cy="50%"
//                       outerRadius={100}
//                       labelLine={false}
//                       label={({ name, percent }) =>
//                         `${name}: ${(percent * 100).toFixed(0)}%`
//                       }
//                       dataKey="value"
//                     >
//                       {charts.prStatusFunnel?.map((entry, i) => (
//                         <Cell key={i} fill={entry.fill} />
//                       ))}
//                     </Pie>
//                     <Legend />
//                   </PieChart>
//                 </ResponsiveContainer>
//               ) : (
//                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
//                   No Data Found
//                 </div>
//               )}
//             </div>
//           </CardContent>
//         </Card>

//         {/* PO Status Distribution */}
//         <Card>
//           <CardHeader>
//             <CardTitle>PO Status Distribution</CardTitle>
//             <CardDescription>Open vs Closed purchase orders</CardDescription>
//           </CardHeader>
//           <CardContent>
//             <div style={{ height: '300px' }}>
//               {charts.poStatusData?.length > 0 ? (
//                 <ResponsiveContainer width="100%" height="100%">
//                   <PieChart>
//                     <Pie
//                       data={charts.poStatusData}
//                       cx="50%"
//                       cy="50%"
//                       outerRadius={100}
//                       labelLine={false}
//                       label={({ name, percent }) =>
//                         `${name}: ${(percent * 100).toFixed(0)}%`
//                       }
//                       dataKey="value"
//                     >
//                       {charts.poStatusData?.map((entry, i) => (
//                         <Cell key={i} fill={entry.fill} />
//                       ))}
//                     </Pie>
//                     <Legend />
//                   </PieChart>
//                 </ResponsiveContainer>
//               ) : (
//                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
//                   No Data Found
//                 </div>
//               )}
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Monthly PO Trend */}
//       <Card style={{ marginBottom: '24px' }}>
//         <CardHeader>
//           <CardTitle>Monthly PO Trend</CardTitle>
//           <CardDescription>Purchase orders generated per month</CardDescription>
//         </CardHeader>
//         <CardContent>
//           <div style={{ height: '300px' }}>
//             {charts.monthlyPOTrend?.length > 0 ? (
//               <ResponsiveContainer width="100%" height="100%">
//                 <LineChart data={charts.monthlyPOTrend}>
//                   <CartesianGrid strokeDasharray="3 3" />
//                   <XAxis dataKey="month" />
//                   <YAxis />
//                   <Legend />
//                   <Line
//                     type="monotone"
//                     dataKey="count"
//                     stroke="hsl(217, 91%, 60%)"
//                     strokeWidth={2}
//                     name="POs Generated"
//                   />
//                 </LineChart>
//               </ResponsiveContainer>
//             ) : (
//               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
//                 No Data Found
//               </div>
//             )}
//           </div>
//         </CardContent>
//       </Card>

//       {/* Top Vendors */}
//       <Card style={{ marginBottom: '24px' }}>
//         <CardHeader>
//           <CardTitle>Top Vendors</CardTitle>
//           <CardDescription>Most active vendors by PO count</CardDescription>
//         </CardHeader>
//         <CardContent>
//           <div style={{ height: '300px' }}>
//             {charts.topVendors?.length > 0 ? (
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={charts.topVendors}>
//                   <CartesianGrid strokeDasharray="3 3" />
//                   <XAxis dataKey="vendor" />
//                   <YAxis />
//                   <Legend />
//                   <Bar
//                     dataKey="count"
//                     fill="hsl(142, 71%, 45%)"
//                     name="PO Count"
//                   />
//                 </BarChart>
//               </ResponsiveContainer>
//             ) : (
//               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
//                 No Data Found
//               </div>
//             )}
//           </div>
//         </CardContent>
//       </Card>

//       {/* Overdue by Department */}
//       <Card>
//         <CardHeader>
//           <CardTitle>Overdue PRs by Department</CardTitle>
//           <CardDescription>Department-wise overdue purchase requests</CardDescription>
//         </CardHeader>
//         <CardContent>
//           <div style={{ height: '300px' }}>
//             {charts.overdueDepartments?.length > 0 ? (
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={charts.overdueDepartments}>
//                   <CartesianGrid strokeDasharray="3 3" />
//                   <XAxis dataKey="department" />
//                   <YAxis />
//                   <Legend />
//                   <Bar
//                     dataKey="count"
//                     fill="hsl(0, 84%, 60%)"
//                     name="Overdue Count"
//                   />
//                 </BarChart>
//               </ResponsiveContainer>
//             ) : (
//               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
//                 No Data Found
//               </div>
//             )}
//           </div>
//         </CardContent>
//       </Card>

//       <style jsx>{`
//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }
//       `}</style>
//     </div>
//   );
// }
